import { Box, Link, Stack } from "@mui/material";
import { Link as LinkBase } from "react-router-dom";
import { FC, useMemo } from "react";
import { ACTIONS, STEPS } from "../config";
import { FieldWithEntitySelectorAndTabs } from "components/FieldWithEntitySelector";
import { REQUIREMENT } from "helpers/constants";
import { User } from "types/user";
import { Requirement } from "types/requirement";
import { StepLayoutContainer } from "components/Layout/StepLayoutContainer";
import { ErrorType, RequestFeedbackData } from "../types";
import { getRequirementDetailsPath } from "utils/requirementPath";

type Props = {
  onUpdate: (data: any) => void;
  data: RequestFeedbackData;
  learners: User[];
  competencySkills: Requirement[];
  handleStep: (step: string, action?: string) => void;
  error: ErrorType;
};

export const RequestLearnerAndRequirementStep: FC<Props> = ({
  onUpdate,
  data,
  learners,
  competencySkills,
  handleStep,
  error,
}) => {
  const isITER = useMemo(
    () => data?.selectedCompetencySkill?.type === REQUIREMENT.REQUIREMENT_ITER,
    [data?.selectedCompetencySkill?.type],
  );

  const nextStep = useMemo(() => {
    return isITER ? STEPS.REQUEST1 : STEPS.REQUEST;
  }, [isITER]);

  const requirementDetailsPath = getRequirementDetailsPath(
    data.selectedCompetencySkill,
  );

  return (
    <StepLayoutContainer
      title="Select the learner and the competency to evaluate"
      onClickNext={() => handleStep(nextStep)}
    >
      <Box mt={3}>
        <FieldWithEntitySelectorAndTabs
          label="Learner"
          voiceFieldId="selectedLearner"
          element={data.selectedLearner}
          entityList={learners}
          entityName="Learner"
          idField="user_id"
          onChangeElement={(element) => {
            onUpdate({
              type: ACTIONS.SET_SELECTED_LEARNER,
              payload: element,
            });
          }}
          error={error.selectedLearner ?? ""}
          showPet
        />
      </Box>

      <Box mt={3}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box flex={1}>
            <FieldWithEntitySelectorAndTabs
              label="Select a Requirement"
              voiceFieldId="selectedCompetencySkill"
              element={data.selectedCompetencySkill}
              entityList={competencySkills}
              entityName="Requirement"
              idField="requirement_id"
              showDomainSquares
              onChangeElement={(element) => {
                onUpdate({
                  type: ACTIONS.SET_COMPETENCY_SKILLS,
                  payload: element,
                });
              }}
              tabs={[
                {
                  label: "EPA",
                  filterTabCriteria: (r: any) =>
                    r?.type === REQUIREMENT.REQUIREMENT_EPA,
                },
                {
                  label: "Competency",
                  filterTabCriteria: (r: any) =>
                    r?.type === REQUIREMENT.REQUIREMENT_COMPETENCY,
                },
                {
                  label: "ITER",
                  filterTabCriteria: (r: any) =>
                    r?.type === REQUIREMENT.REQUIREMENT_ITER,
                },
              ]}
              error={error.selectedCompetencySkill ?? ""}
            />
          </Box>
          {requirementDetailsPath && (
            <Link
              component={LinkBase}
              to={requirementDetailsPath}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
            >
              View more
            </Link>
          )}
        </Stack>
      </Box>
    </StepLayoutContainer>
  );
};
